import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { validateEmail } from "../utils/validators";
import Button from "../components/common/Button";
import Input from "../components/common/Input";
import { Card } from "../components/ui/Card";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Toast from "../components/common/Toast";
import { useTheme } from "../context/ThemeContext";

const FORGOT_PASSWORD_ENDPOINTS = [
  `${import.meta.env.VITE_BACKEND_API_BASE_URL || ""}/forgot-password.php`,
  `${import.meta.env.VITE_AUTH_API_BASE_URL || ""}/forgot-password.php`,
  "https://gainwisefx.com/api/forgot-password.php",
  "/api/forgot-password.php",
].filter((value, index, list) => value && list.indexOf(value) === index);

const ForgotPassword = () => {
  const [formData, setFormData] = useState({
    email: "",
  });
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const navigate = useNavigate();
  const { isDark, getThemeConfig } = useTheme();
  const themeConfig = getThemeConfig();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({}); // Clear any previous errors

    try {
      let apiResult = null;

      for (const endpoint of FORGOT_PASSWORD_ENDPOINTS) {
        try {
          const response = await fetch(endpoint, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              email: formData.email,
            }),
          });

          if (!response.ok) {
            continue;
          }

          apiResult = await response.json();
          break;
        } catch {
          continue;
        }
      }

      if (!apiResult) throw new Error("Network response was not ok");

      if (apiResult.success) {
        setIsSubmitted(true);
        setToast({
          type: "success",
          message:
            apiResult.message ||
            "Password reset instructions have been sent to your email.",
        });

      } else {
        // Handle specific error cases
        const errorMessage =
          apiResult.message || "Failed to send reset email. Please try again.";

        if (errorMessage.toLowerCase().includes("email")) {
          setErrors({ email: errorMessage });
        } else if (
          errorMessage.toLowerCase().includes("rate limit") ||
          errorMessage.toLowerCase().includes("too many")
        ) {
          setToast({
            type: "error",
            message:
              "Too many reset attempts. Please wait before trying again.",
          });
        } else {
          setToast({
            type: "error",
            message: errorMessage,
          });
        }
      }
    } catch (error) {
      // Handle network errors
      if (!navigator.onLine) {
        setToast({
          type: "error",
          message:
            "No internet connection. Please check your network and try again.",
        });
      } else if (
        error.name === "TypeError" &&
        error.message.includes("fetch")
      ) {
        setToast({
          type: "error",
          message: "Unable to connect to server. Please try again later.",
        });
      } else {
        setToast({
          type: "error",
          message: "An unexpected error occurred. Please try again.",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  return (
    <div
      className={`min-h-screen flex items-center justify-center p-4 ${
        isDark
          ? "bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900"
          : "bg-gradient-to-br from-blue-50 via-white to-blue-50"
      }`}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <Card
          className={`backdrop-blur-sm ${
            isDark
              ? "bg-slate-800/50 border-slate-700/50"
              : "bg-white/80 border-gray-200/50"
          }`}
        >
          <div className="p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5 }}
                className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <span className="text-2xl font-bold text-white">T</span>
              </motion.div>
              <h1
                className={`text-2xl font-bold mb-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                {isSubmitted ? "Check Your Email" : "Forgot Password?"}
              </h1>
              <p className={isDark ? "text-slate-400" : "text-gray-600"}>
                {isSubmitted
                  ? "We've sent password reset instructions to your email address."
                  : "Enter your email address and we'll send you a link to reset your password."}
              </p>
            </div>

            {!isSubmitted ? (
              /* Reset Form */
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Input
                    type="email"
                    name="email"
                    placeholder="Enter your email address"
                    value={formData.email}
                    onChange={handleInputChange}
                    error={errors.email}
                    className={
                      isDark
                        ? "bg-slate-700/50 border-slate-600 text-white placeholder-slate-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center space-x-2">
                      <LoadingSpinner size="sm" />
                      <span>Sending Reset Link...</span>
                    </div>
                  ) : (
                    "Send Reset Link"
                  )}
                </Button>
              </form>
            ) : (
              /* Success State */
              <div className="space-y-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      className="w-8 h-8 text-green-600 dark:text-green-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <p
                    className={`text-sm ${
                      isDark ? "text-slate-300" : "text-gray-600"
                    }`}
                  >
                    If an account with that email exists, you'll receive
                    password reset instructions within a few minutes.
                  </p>
                </div>

                <div className="space-y-3">
                  <p
                    className={`text-sm ${
                      isDark ? "text-slate-400" : "text-gray-500"
                    }`}
                  >
                    Didn't receive the email? Check your spam folder or try
                    again with a different email address.
                  </p>

                  <Button
                    onClick={() => {
                      setIsSubmitted(false);
                      setFormData({ email: "" });
                      setErrors({});
                    }}
                    variant="outline"
                    className={`w-full ${
                      isDark
                        ? "text-white border-slate-600 hover:bg-slate-700"
                        : "text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    Try Different Email
                  </Button>
                </div>
              </div>
            )}

            {/* Back to Login Link */}
            <div className="mt-6 text-center">
              <p className={isDark ? "text-slate-400" : "text-gray-600"}>
                Remember your password?{" "}
                <Link
                  to="/login"
                  className={`font-medium transition-colors ${
                    isDark
                      ? "text-blue-400 hover:text-blue-300"
                      : "text-blue-600 hover:text-blue-500"
                  }`}
                >
                  Back to Sign In
                </Link>
              </p>
            </div>
          </div>
        </Card>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className={`mt-6 text-center text-sm ${
            isDark ? "text-slate-400" : "text-gray-500"
          }`}
        >
          <p>
            Need help?{" "}
            <Link
              to="/contact"
              className={
                isDark
                  ? "text-blue-400 hover:text-blue-300"
                  : "text-blue-600 hover:text-blue-500"
              }
            >
              Contact Support
            </Link>
          </p>
        </motion.div>
      </motion.div>

      {/* Toast Notifications */}
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default ForgotPassword;
